import type {
  DocumentPagesParams,
  UploadDocumentVisibility,
} from "@common/api/documents"
import type { ArchetypeName, DocumentFilters } from "@common/client"
import { z } from "zod"
import {
  type Column,
  columnSchema,
  DEFAULT_COLUMNS,
  SORT_KEYS,
  withFixed,
} from "./columns"
import { type Grouping, groupingSchema } from "./grouping"
import { readDocumentsVisibility, VISIBILITY_LABELS } from "./preferences"

const SEPARATE_ARCHETYPES: ArchetypeName[] = ["airworthiness_directive"]

const MAX_SEARCH_LENGTH = 255

export const TABS = ["user", "tenant", "global", "archived"] as const

const tabSchema = z.enum(TABS)

const directionSchema = z.enum(["asc", "desc"])

export type Direction = z.infer<typeof directionSchema>

export type Tab = z.infer<typeof tabSchema>

export const documentsSearchSchema = z.object({
  tab: tabSchema.optional().catch(undefined),
  search: z.string().optional().catch(undefined),
  manufacturer: z.array(z.string()).optional().catch(undefined),
  ata_chapter: z.array(z.number()).optional().catch(undefined),
  group: groupingSchema.optional().catch(undefined),
  subgroup: groupingSchema.optional().catch(undefined),
  columns: z.array(columnSchema).optional().catch(undefined),
  sort: columnSchema.optional().catch(undefined),
  direction: directionSchema.optional().catch(undefined),
})

export type DocumentsSearch = z.infer<typeof documentsSearchSchema>

export type DocumentsDisplay = {
  group: Grouping | undefined
  subgroup: Grouping | undefined
  columns: Column[]
  sort: Column
  direction: Direction
}

export function parseDocumentsSearch(
  search: Record<string, unknown>,
): DocumentsSearch {
  const parsed = documentsSearchSchema.parse(search)
  return { ...parsed, tab: parsed.tab ?? readDocumentsVisibility() }
}

export function tabLabel(tab: Tab): string {
  switch (tab) {
    case "user":
    case "tenant":
    case "global":
      return VISIBILITY_LABELS[tab]
    case "archived":
      return "Archived"
  }
}

export function uploadVisibility(tab: Tab): UploadDocumentVisibility | null {
  switch (tab) {
    case "user":
    case "tenant":
      return tab
    case "global":
    case "archived":
      return null
  }
}

export function tabFilters(tab: Tab): DocumentFilters {
  const archived = tab === "archived"
  return {
    visibility: archived ? null : tab,
    archived,
    superseded: true,
    exclude_archetypes: archived ? [] : SEPARATE_ARCHETYPES,
  }
}

export function displayOf(search: DocumentsSearch): DocumentsDisplay {
  return {
    group: search.group,
    subgroup:
      search.group && search.subgroup !== search.group
        ? search.subgroup
        : undefined,
    columns: search.columns ? withFixed(search.columns) : DEFAULT_COLUMNS,
    sort: search.sort ?? "added",
    direction: search.direction ?? (search.sort ? "asc" : "desc"),
  }
}

export function documentPagesParams(
  search: DocumentsSearch,
): DocumentPagesParams {
  const display = displayOf(search)
  return {
    ...tabFilters(search.tab ?? "user"),
    search: search.search?.slice(0, MAX_SEARCH_LENGTH) || null,
    attributes: {
      manufacturer: search.manufacturer ?? [],
      ata_chapter: search.ata_chapter ?? [],
    },
    order_by: SORT_KEYS[display.sort] ?? "created_at",
    direction: display.direction,
    group: display.group ?? null,
    subgroup: display.subgroup ?? null,
  }
}
